/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { SubProduct } from '@jira/platform__common-constants/src/sub-product-types.tsx';
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';

/**
 * Maps a given project type to its corresponding sub-product. This function is designed to
 * facilitate the identification of product categories based on project characteristics.
 * Special cases are handled explicitly, with a fallback to 'unknown' for unlisted types,
 * ensuring broad applicability across different project contexts.
 */
export const subProductFromProjectType = (projectType: ProjectType): SubProduct => {
	switch (projectType) {
		case 'business':
			return 'core';
		case 'product_discovery': // for now, product discovery/polaris is bound to JSW
		case 'software':
			return 'software';
		case 'service_desk':
			return 'serviceDesk';
		case 'customer_service':
			return 'customer-service';
		default:
	}

	return 'unknown';
};

export const useSubProduct = (): SubProduct | null => {
	const { data } = useProjectContext();

	return data ? subProductFromProjectType(data.projectType) : null;
};

/**
 * A custom mapping function that translates project types to sub-products specifically for Analytics.
 * This function is tailored to ensure that the 'product_discovery' project type is consistently
 * mapped to the 'polaris' sub-product. In contrast, the original `subProductFromProjectType` is used in places
 * where the 'product_discovery' project type is associated with 'software'. (like billing related code)
 * This distinction is crucial for maintaining accurate analytics and reporting.
 */
export const subProductFromProjectTypeForAnalytics = (projectType: ProjectType): SubProduct => {
	if (projectType === 'product_discovery') {
		return 'polaris';
	}

	return subProductFromProjectType(projectType);
};

export const useSubProductForAnalytics = (): SubProduct | null => {
	const { data } = useProjectContext();

	return data ? subProductFromProjectTypeForAnalytics(data.projectType) : null;
};
